import CustomerInformation from './Components/CustomerInformation/CustomerInformation';
import AccountDeletion from './Components/Profile/AccountDeletion';
import Resources from './Components/Resources/Resources';
import NativeSessionActivity from './Components/NativeSessionActivity';
import { Backdrop, CircularProgress, Stack, Typography } from '@mui/material';
import NativeSetup from './Components/NativeSetup';
import OfflineBanner from './Components/OfflineBanner';
import './App.css';
import React, { useEffect, useState } from 'react';
import Dashboard from './Components/Dashboard/Dashboard.js';
import MapPage from './Components/Maps/MapPage';
import Header from './Components/MainHeader.js';
import { Routes, Route, Navigate, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from './store/authSlice';
import Login from './Components/Login.js';
import FirstLoginFlow from './Components/FirstLoginFlow/FirstLoginFlow.js';
import CreateAccount from './Components/CreateAccount/CreateAccount.js';
import ForgotPassword from './Components/ResetPassword/ForgotPassword.js';
import ResetPassword from './Components/ResetPassword/ResetPassword.js';
import CaregiverManagement from './Components/AdminNav/CaregiverManagement.js';
import AddCaregiver from './Components/AdminNav/AddCaregiver.js';
import RecentChanges from './Components/AdminNav/RecentChanges.js';
import NotificationsPage from './Components/Notifications/NotificationsPage.js';
import NotificationPreferences from './Components/Notifications/NotificationPreferences.js';
import Profile from './Components/Profile/ProfileRoute.jsx';
import AppLayout from './Components/Layout/AppLayout.js';
import PWAUpdateManager from './Components/PWA/PWAUpdateManager.js';
import DevIndicator from './Components/DevIndicator/DevIndicator.js';
import PushNotificationSetup from './Components/Notifications/PushNotificationSetup.js';
import BottomNavBar from './Components/AdminNav/BottomNavBar.js';
import { usePWAStatus } from './hooks/usePWAStatus';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Activity from './Components/Activity/Activity.js';
import { hasActivityAccess } from './utils/activityAccess.js';
import { notifications, preferences, runtime, sessionStore, links } from './platform';

const PWA_NOTIFICATION_PROMPT_SHOWN_KEY = 'connectApp365PwaNotificationPromptShown';

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const [pendingPush, setPendingPush] = useState(false);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const setupUserId = useSelector((state) => state.auth.user?.user);
    const signedEula = useSelector((state) => state.auth.signedEula);
    const deviceLogoutPending = useSelector(state => state.auth.deviceLogoutPending);
    const authChecking = useSelector((state) => state.auth.authChecking);
    const activeUnit = useSelector((state) => state.auth.activeUnit);
    const dispatch = useDispatch();
    const { isStandalone } = usePWAStatus();
    const isAppShell = runtime.isNative() || isStandalone;
    const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
    const isProfileRoute = location.pathname.startsWith('/profile');
    const isAccountRoute = isProfileRoute || location.pathname === '/account-deletion';
    const showAccountControls = isAuthenticated && signedEula &&
        ['/dashboard', '/resources', '/map', '/customer-information', '/activity', '/caregivers/:customerKey', '/caregivers/:customerKey/add',
            '/caregivers/:customerKey/changes', '/profile', '/profile/:tab'].some(path => matchPath(path, location.pathname));

    useEffect(() => {
        if (!runtime.isNative()) return;
        return notifications.onAction(() => setPendingPush(true));
    }, []);
    useEffect(() => {
        if (pendingPush && isAuthenticated && signedEula && !authChecking) {
            setPendingPush(false);
            navigate(links.notificationRoute());
        }
    }, [pendingPush, isAuthenticated, signedEula, authChecking, navigate]);

    // Note: Token validation is now handled by authMiddleware.js
    // The middleware automatically validates the token on app init and rehydrates state

    // Keep the return destination outside Profile and its account-deletion flow.
    useEffect(() => {
        if (!isAccountRoute &&
            location.pathname !== '/login' && 
            location.pathname !== '/createAccount' &&
            location.pathname !== '/forgotPassword' &&
            location.pathname !== '/resetPassword' &&
            location.pathname !== '/eula' &&
            location.pathname !== '/' &&
            isAuthenticated) {
            void sessionStore.set('lastNonProfileRoute', location.pathname);
        }
    }, [location.pathname, isAuthenticated, isAccountRoute]);

    useEffect(() => {
        if (runtime.isNative()) return; // Native device sessions have their own expiry and biometric lock.
        // Setup auto-logout for inactivity
        let idleTimeout = setTimeout(() => {
            dispatch(logout());
        }, 24 * 60 * 60 * 1000); // 24 hours in milliseconds

        // Reset timeout on user activity
        const handleUserActivity = () => {
            clearTimeout(idleTimeout);
            idleTimeout = setTimeout(() => {
                dispatch(logout());
            }, 24 * 60 * 60 * 1000);
        };

        document.addEventListener('mousemove', handleUserActivity);
        document.addEventListener('keypress', handleUserActivity);

        return () => {
            document.removeEventListener('mousemove', handleUserActivity);
            document.removeEventListener('keypress', handleUserActivity);
            clearTimeout(idleTimeout);
        };
    }, [dispatch]);

    useEffect(() => {
        if (runtime.isNative() || !isAuthenticated || !signedEula || !isAppShell) {
            return;
        }

        if (!notifications.isSupported()) return;

        let cancelled = false;
        const preparePrompt = async () => {
            const promptAlreadyShown = await preferences.get(PWA_NOTIFICATION_PROMPT_SHOWN_KEY);
            if (promptAlreadyShown === true || cancelled) return;

            await preferences.set(PWA_NOTIFICATION_PROMPT_SHOWN_KEY, true);
            if (!cancelled) setShowNotificationPrompt(true);
        };

        void preparePrompt();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, signedEula, isAppShell]);

    // Show loading spinner while validating token
    if (authChecking) {
        return (
            <>
            <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}><OfflineBanner /></div>
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh'
            }}>
                <div className="loading-spinner"></div>
            </div>
            </>
        );
    }

    return (
        <React.Fragment>
            <Header showAccountControls={showAccountControls} />
            <AppLayout>
                {runtime.isNative() && deviceLogoutPending && location.pathname !== '/account-deletion' ? (
                    <Backdrop open aria-hidden={false} sx={{ zIndex: theme => theme.zIndex.modal + 1, bgcolor: 'background.default', color: 'text.primary' }}>
                        <Stack alignItems="center" spacing={2} role="status" aria-live="polite">
                            <CircularProgress aria-label="Logging out" />
                            <Typography>Logging out…</Typography>
                        </Stack>
                    </Backdrop>
                ) : <Routes>
                    <Route path="" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
                    <Route path="/login" element={isAuthenticated ? <Navigate to={signedEula ? "/dashboard" : "/eula"} replace /> : <Login />} />
                    <Route path="/eula" element={isAuthenticated && (!signedEula) ? <FirstLoginFlow /> : <Navigate to="/dashboard" />} />
                    <Route path="/dashboard" element={isAuthenticated && signedEula ? <Dashboard /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/resources" element={isAuthenticated && signedEula ? <Resources /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/customer-information" element={isAuthenticated && signedEula ? <CustomerInformation /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/map" element={isAuthenticated && signedEula ? <MapPage /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route
                        path="/activity"
                        element={
                            isAuthenticated && signedEula && hasActivityAccess(activeUnit)
                                ? <Activity />
                                : <Navigate to={isAuthenticated ? (signedEula ? "/dashboard" : "/eula") : "/login"} replace />
                        }
                    />
                    <Route path="/createAccount" element={<CreateAccount />} />
                    <Route path="/forgotPassword" element={<ForgotPassword />} />
                    <Route path="/account-deletion" element={<AccountDeletion />} />
                    <Route path="/resetPassword" element={<ResetPassword key={location.search} />} />
                    {/* Admin Routes - Protected by userType check in components */}
                    <Route path="/caregivers/:customerKey" element={isAuthenticated && signedEula ? <CaregiverManagement /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/caregivers/:customerKey/add" element={isAuthenticated && signedEula ? <AddCaregiver /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/caregivers/:customerKey/changes" element={isAuthenticated && signedEula ? <RecentChanges /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    
                    {/* Profile Routes - 3 tab structure */}
                    <Route path="/profile" element={isAuthenticated && signedEula ? <Profile /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    <Route path="/profile/:tab" element={isAuthenticated && signedEula ? <Profile /> : <Navigate to={isAuthenticated ? "/eula" : "/login"} />} />
                    
                    {/* Backward compatibility redirects for old notification routes */}
                    <Route path="/notifications" element={<Navigate to="/profile/notifications" replace />} />
                    <Route path="/notifications/preferences" element={<Navigate to="/profile/preferences" replace />} />
                    <Route path="/notification-preferences" element={<Navigate to="/profile/preferences" replace />} />
                </Routes>}
            </AppLayout>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            {runtime.isWeb() && <PWAUpdateManager />}
            <DevIndicator />
            {isAppShell && isAuthenticated && signedEula && isAccountRoute && (
                <BottomNavBar />
            )}
            {runtime.isNative() && isAuthenticated && signedEula && <NativeSessionActivity key={setupUserId} />}
            {runtime.isNative() && isAuthenticated && signedEula && <NativeSetup key={setupUserId} userId={setupUserId} />}
            <PushNotificationSetup
                open={showNotificationPrompt}
                onClose={() => setShowNotificationPrompt(false)}
            />
        </React.Fragment>
    );
}

export default App;
